import { TopicNode, MessagePathNode } from "@/shared/domain/topics";

import {
  FilterResult,
  isMessagePathFilterResult,
  isTopicFilterResult,
} from "../filterResults";

export interface TopicTreeNode<T> {
  /** The children of the node. Always a `MessagePathNode`. */
  children: TopicTreeNode<MessagePathNode>[];

  /** Whether the node should be displayed */
  hidden: boolean;

  /** The domain entity this node represents */
  data: T;

  /** Match metadata if the node matched a filter */
  match?: {
    /**
     * The [start, end] indices of the node's label
     * that matched the keyword filter
     */
    position: [number, number];
  };

  /** Whether the node's children should be shown by default */
  showChildren: boolean;
}

export interface BuildTopicTreeProps {
  /** The topic nodes to build the tree from */
  topicNodes: TopicNode[];

  /** The filter results to use for highlighting and visibility */
  filterResults: FilterResult[];

  /**
   * Whether the query is empty or not.
   * Used to determine the node's default visibility.
   */
  isQueryEmpty: boolean;
}

/**
 * Builds a tree for displaying the topics and message paths in the UI.
 *
 * It wraps the TopicNode and MessagePathNode domain entities with additional UI state
 * specific to rendering the topic tree.
 */
export function buildTopicTree(
  props: BuildTopicTreeProps,
): TopicTreeNode<TopicNode>[] {
  const { topicNodes, filterResults, isQueryEmpty } = props;

  const nodeIdToMatch = createResultIdToMatchMap(filterResults);

  function createNodeState(
    nodeId: string,
    children: TopicTreeNode<MessagePathNode>[],
  ): {
    hidden: boolean;
    match: FilterResult["match"];
    showChildren: boolean;
  } {
    if (isQueryEmpty) {
      return { hidden: false, match: undefined, showChildren: false };
    }

    const match = nodeIdToMatch.get(nodeId);
    const hasVisibleChildren = children.some((node) => !node.hidden);
    const isVisible = match !== undefined || hasVisibleChildren;

    return {
      hidden: !isVisible,
      match,
      showChildren: hasVisibleChildren,
    };
  }

  function createTreeFromMessagePathNode(
    messagePathNode: MessagePathNode,
  ): TopicTreeNode<MessagePathNode> {
    const children = messagePathNode.children.map(
      createTreeFromMessagePathNode,
    );
    const { hidden, match, showChildren } = createNodeState(
      messagePathNode.id,
      children,
    );

    return {
      children,
      hidden,
      match,
      data: messagePathNode,
      showChildren,
    };
  }

  return topicNodes.map((topicNode) => {
    const children = topicNode.children.map(createTreeFromMessagePathNode);
    const { hidden, match, showChildren } = createNodeState(
      topicNode.id,
      children,
    );

    return {
      children,
      hidden,
      match,
      data: topicNode,
      showChildren,
    };
  });
}

/**
 * Creates a map of filter result ids to their match metadata.
 *
 * This is used as an optimization to avoid repeatedly searching the filter results array
 * for matches.
 */
function createResultIdToMatchMap(results: FilterResult[]) {
  const resultIdToMatch = new Map<string, FilterResult["match"]>();
  for (const result of results) {
    if (isTopicFilterResult(result)) {
      resultIdToMatch.set(result.topic.topic_id, result.match);
    } else if (isMessagePathFilterResult(result)) {
      resultIdToMatch.set(result.messagePath.message_path_id, result.match);
    }
  }
  return resultIdToMatch;
}
